import React from 'react';
import {
  checkPermission,
  filterSearch,
  getMomentTimezone,
  showConfirm,
  showErrorMessage,
  sortDate,
} from './utilHelpers';
import { linkify } from 'components/common/DraftEditor/util/helpers';

import moment from 'moment';
import Immutable from 'immutable';
import { Tooltip } from 'antd';
import IntlMessages from 'util/IntlMessages';
import { getIntlMessage } from 'helpers/utilHelpers';
import { TASK } from '../constants/RbacPermissions';
import { EditorState } from 'draft-js';
import { compositeDecorator } from 'components/common/DraftEditor/util/decorators';
import { convertFromRaw } from 'draft-js';
import { dateFormat, timeFormat } from 'util/config';

export const getCreateSubTasksUrl = (task) => {
  return `/projects/${task.get('projectId')}/tasks/create?parentId=${task.get('taskId')}`;
};

export const getCreateCopyTasksUrl = (task) => {
  return `/projects/${task.get('projectId')}/tasks/${task.get('taskId')}/update?copy=1`;
};

export const getTaskPermissions = ({ task, project, currentUser }) => {
  if (!task || !project) {
    return {
      canUpdate: false,
      canCloseTask: false,
      canChangeUser: false,
      canChangeDate: false,
      canChangeStatus: false,
      canChangeLabels: false,
      canChangePriority: false,
    };
  }

  // Check taskDelete permission for check taskFull group
  let fullAccess = !!checkPermission(TASK.delete);
  const prefix = currentUser.get('userType') === 'client' ? 'Client' : 'User';

  if (project.get('userId') === currentUser.get('userId') && !fullAccess) {
    fullAccess = true;
  }

  if (project.get(`can${prefix}Update`) && !fullAccess) {
    fullAccess = !!checkPermission(TASK.updateOwn, { model: task });
  }

  return {
    canUpdate: fullAccess,
    canCloseTask: fullAccess || !!project.get(`can${prefix}CloseTask`),
    canChangeUser: fullAccess || !!project.get(`can${prefix}ChangeUser`),
    canChangeDate: fullAccess || !!project.get(`can${prefix}ChangeDate`),
    canChangeStatus: fullAccess || !!project.get(`can${prefix}ChangeStatus`),
    canChangeLabels: fullAccess || !!project.get(`can${prefix}ChangeLabels`),
    canChangePriority: fullAccess || !!project.get(`can${prefix}ChangePriority`),
  };
};

// Function check if task have any description text
export const checkDescription = (description) => {
  if (!description) {
    return false;
  }
  if (description.children && description.children[0]) {
    if (description.children.length !== 1) {
      return true;
    }
    description = description.children[0];
    if (description.children && description.children[0]) {
      if (description.children.length !== 1) {
        return true;
      }
      description = description.children[0];
      return description.text !== '';
    }
    return description.text !== '';
  }
  return false;
};

// Function for sort sub tasks in task.
export const sortSubTasks = (parentTask, sorting) => {
  const subTasks = parentTask?.get('subTasks');
  if (subTasks?.size) {
    return parentTask.set(
      'subTasks',
      subTasks
        ?.map((subTask) => {
          return sortSubTasks(subTask, sorting);
        })
        ?.sort((a, b) => {
          if (sorting?.get('value') && a && b) {
            const ascending = sorting.get('value') === 'ascending' ? 1 : -1;

            if (sorting.get('key') === 'priority') {
              return a.get('priority') < b.get('priority') ? -ascending : ascending;
            }

            if (sorting.get('key') === 'timeCreated') {
              const result = sortDate(a.get('timeCreated'), b.get('timeCreated'));
              return ascending === 1 ? -result : result;
            }

            if (sorting.get('key') === 'endDate') {
              const result = sortDate(a.get('endDate'), b.get('endDate'));
              return ascending === 1 ? -result : result;
            }
          }
          return 1;
        }),
    );
  }

  return parentTask;
};

// Function for filtered task and sub tasks by search
export const searchTaskFilter = (task, search) => {
  if (!task) {
    return null;
  }

  if (!search) {
    return task;
  }

  const fullHeader = task.get('header') + task.get('startDate') + task.get('endDate');
  let result = fullHeader?.toLowerCase()?.indexOf(search.toLowerCase()) !== -1;

  if (task.get('subTasks')?.size) {
    let subTasks = task.get('subTasks');

    subTasks = subTasks.map((subTask) => searchTaskFilter(subTask, search)).filter((subTask) => subTask);

    if (subTasks?.size) {
      result = true;
    }

    task = task.set('subTasks', subTasks);
  }

  return result ? task : null;
};

// Function for parse text by links to JSX items
export const fromTextToJSXLinks = (text) => {
  const links = linkify.match(text);

  if (!links) {
    return text;
  }

  const items = [];
  let key = 0;

  links.forEach((link) => {
    const match = text.split(link.text);
    if (match[0] || match[0] === '') {
      items.push(<span key={key++}>{match[0]}</span>);
      items.push(
        <a key={key++} href={link.url} target="_blank" rel="noopener noreferrer">
          {link.text}
        </a>,
      );
      text = match.slice(1).join(link.text);
    }
  });

  items.push(<span key={key++}>{text}</span>);

  return items;
};

/**
 * Filter tasks list in TaskMigrateForm component
 * @param tasks
 * @param search
 * @returns {*}
 */
export const filterMigrateTaskList = (tasks, search) => {
  const prepareTask = (task, search) => {
    if (!Immutable.isMap(task)) {
      return null;
    }

    // TODO: Remove when added filter by status
    if (task.get('status') === 'finished') {
      return null;
    }

    if (!search) {
      return task;
    }

    const fullHeader = task.get('header') + task.get('startDate') + task.get('endDate');
    let result = filterSearch(fullHeader, search);
    task = task.set('makeTransparent', !result);

    if (task.get('subTasks')?.size) {
      let subTasks = task
        .get('subTasks')
        .map((subTask) => prepareTask(subTask, search))
        .filter((subTask) => subTask);

      if (subTasks?.size) {
        result = true;
      }

      task = task.set('subTasks', subTasks);
    }

    return result ? task : null;
  };

  return tasks.map((task) => prepareTask(task, search)).filter((task) => task);
};

// Function render endDate component
export const getFormattedEndDate = (endDate, time = null) => {
  const dateLabel = time ? `${endDate} ${getMomentTimezone(time, timeFormat).format(timeFormat)}` : endDate;

  const format = time ? 'DD.MM.YYYY в HH:mm' : dateFormat;

  return (
    <Tooltip
      title={
        <span className="gx-text-nowrap">
          <IntlMessages id="task.item.endDate" />
        </span>
      }
    >
      {moment(dateLabel, format).calendar(null, {
        sameDay: getIntlMessage('task.form.date.today'),
        nextDay: '2 ' + getIntlMessage('task.twoDays'),
        nextWeek: function(now) {
          if (this.isSame(now.add(2, 'days'), 'day')) {
            return '3 ' + getIntlMessage('task.twoDays');
          }

          return format;
        },
        lastDay: format,
        lastWeek: format,
        sameElse: format,
      })}
    </Tooltip>
  );
};

// Function for updating task field
export const changeField = (key, values, taskId, { changeHandler }) => {
  changeHandler({
    taskId: taskId,
    [key]: values,
  });
};
// Function for meta updating task field
export const changeMetaField = (key, values, taskId, { changeMetaHandler }) => {
  changeMetaHandler({
    values: {
      taskId,
      [key]: values,
    },
  });
};
// Function for updating task endDate
export const dateChange = (value, taskId, { changeHandler, setIsEditDate }) => {
  const date = value ? value.format('DD.MM.YYYY') : null;
  changeHandler({
    taskId: taskId,
    endDate: date,
  });
  setIsEditDate({ id: null, visible: false });
};

// Function to create a URL to view a task
export const getTaskUrl = (task, commonList = false) => {
  if (!task) {
    return null;
  }
  let projectId = task.projectId;
  let taskId = task.taskId;

  if (Immutable.isMap(task)) {
    projectId = task.get('projectId');
    taskId = task.get('taskId');
  }

  let pathname = '/tasks';

  if (taskId) {
    pathname += `/${taskId}`;
  }

  if (projectId) {
    pathname = `/projects/${projectId}` + pathname;
  }

  let additionalParams = {};
  if (commonList) {
    additionalParams = { search: '?commonList=1' };
  }

  return {
    ...additionalParams,
    pathname,
  };
};

/**
 * Function create subTaskIds Set
 * @param {Map({ taskId: List(taskIds) })} datasets
 * @param {int} taskId
 * @returns {Set}
 */
export const getSubTaskIds = (datasets, taskId) => {
  const ids = datasets.get(taskId);
  if (!ids) {
    return Immutable.Set();
  }

  let result = Immutable.Set();
  for (const id of ids) {
    result = ids.concat(getSubTaskIds(datasets, id));
  }

  return result;
};

export const prepareOpenHandler = (handler, taskId) => {
  const values = { taskId, status: 'active' };
  return () =>
    showConfirm({
      content: getIntlMessage('task.open.confirm.content'),
      okAction: () =>
        handler({
          values,
          onErrorFunction: (status, data) => {
            if (status === 422 && !!data['parentId']) {
              showErrorMessage(getIntlMessage('task.open.confirm.content.fail'));
            }
          },
        }),
    });
};

/**
 *
 * @param {Immutable.Map} settings
 * @param {string} settings
 * @returns {number}
 */
export const getCountActiveFilters = (settings, search) => {
  if (!settings) {
    return 0;
  }

  let result = 0;
  if (search) {
    result++;
  }

  let filters = settings.getIn(['filters'], Immutable.List());
  filters = filters.filter((value, key) => key !== 'view' && value !== null);
  if (filters.size) {
    result += filters.size;
  }

  return result;
};

export const getDefaultLabelsIds = (taskLabels) => {
  return (
    taskLabels
      ?.map((label) => label.get('labelId'))
      ?.toList()
      .toJS() || []
  );
};

export const getEditorDescription = (taskDescription) => {
  let taskDescriptionJs = taskDescription?.toJS();
  if (taskDescriptionJs && taskDescriptionJs.blocks) {
    taskDescriptionJs = EditorState.createWithContent(convertFromRaw(taskDescriptionJs), compositeDecorator);
  } else {
    taskDescriptionJs = EditorState.createEmpty(compositeDecorator);
  }

  return taskDescriptionJs;
};

export const TASK_STATUS_EMPTY = Immutable.fromJS({
  statusId: 'empty',
  color: '#e8e8e8',
});

export const TASK_STATUS_FINISHED = Immutable.fromJS({
  statusId: 'finished',
  color: '#f44336',
});

export const TASK_SPECIAL_STATUS_IDS = [TASK_STATUS_EMPTY.get('statusId'), TASK_STATUS_FINISHED.get('statusId')];

export const TASK_VIEW_DEFAULT = 'list';

export const TASK_VIEW_CARDS = 'cards';
export const TASK_VIEW_LIST = 'list';

export const getTaskRouteParams = (location) => {
  let viewTaskId = null;
  let scrollCommentId = null;
  let templateId = null;
  const path = location.pathname.split('/');
  const search = location.hash.split('-');

  if (path[path.length - 2] === 'tasks') {
    viewTaskId = parseInt(path[path.length - 1]);
    scrollCommentId = parseInt(search[search.length - 1]);
  }
  if (path[path.length - 3] === 'templates') {
    templateId = parseInt(path[path.length - 2]);
  }

  return { viewTaskId, scrollCommentId, templateId };
};
